import ServiceLocationsEditor from "@/components/ServiceLocationsEditor";
import { SharedServicesPrices } from "@/components/manager/SharedServicesPrices";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Pencil, Trash2, Users, CalendarClock, AlertTriangle, ChevronDown, Save, Merge, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import { AvailabilityModal } from "@/components/manager/ServicePractitionersPanel";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ACCESS_LABELS = { discovery: "Discovery only", community: "Community only", all_members: "Members only" };

const toIn = (s, patch = {}) => ({
  title: s.title || "", category: s.category || "Wellness", short_description: s.short_description || "",
  description: s.description || "", duration_min: s.duration_min || 60, price: s.price || 0,
  member_discount_pct: s.member_discount_pct || 0, deposit_pct: s.deposit_pct || 0,
  access_level: s.access_level || "public", image: s.image || "", room: s.room || "",
  service_type: s.service_type || "foundation", allow_provider_choice: !!s.allow_provider_choice,
  by_appointment: s.by_appointment !== false, published: s.published ?? true,
  focus_areas: s.focus_areas || [], booking_policy: s.booking_policy || {},
  ...patch,
});

export default function UnifiedServicesPanel() {
  const qc = useQueryClient();
  const svcQ = useQuery({ queryKey: ["mgr-my-services"], queryFn: () => api.get("/manager/me/services").then((r) => r.data), refetchInterval: 30000 });
  const spQ = useQuery({ queryKey: ["mgr-service-practitioners"], queryFn: () => api.get("/manager/me/service-practitioners").then((r) => r.data), refetchInterval: 30000 });
  const [modal, setModal] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [mergeGroup, setMergeGroup] = useState(null);
  const [availFor, setAvailFor] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["mgr-my-services"] });
    qc.invalidateQueries({ queryKey: ["mgr-service-practitioners"] });
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/manager/services/${delTarget.id}`);
      toast.success("Service deleted");
      setDelTarget(null);
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.bookings) { setBlocked({ service: delTarget, ...detail }); setDelTarget(null); }
      else toast.error(formatApiError(detail));
    } finally { setDeleting(false); }
  };

  const togglePublished = async (s) => {
    try {
      await api.put(`/manager/services/${s.id}`, toIn(s, { published: !s.published }));
      toast.success(s.published ? "Service deactivated — it's hidden from booking" : "Service activated");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (svcQ.isLoading || spQ.isLoading) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#144F75] inline" /></div>;

  const services = svcQ.data || [];
  const roster = spQ.data?.roster || [];
  const assignById = Object.fromEntries((spQ.data?.services || []).map((s) => [s.id, s]));

  // Duplicate detection: same (trimmed, case-insensitive) name at this center.
  const groups = {};
  services.forEach((s) => {
    const k = (s.title || "").trim().toLowerCase();
    if (k) (groups[k] = groups[k] || []).push(s);
  });
  const dupGroups = Object.values(groups).filter((g) => g.length > 1);
  const dupIds = new Set(dupGroups.flat().map((s) => s.id));

  return (
    <div data-testid="mgr-unified-services">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Sparkles className="text-[#144F75]" size={22} /> Services</h2>
        <button onClick={() => setModal({})} data-testid="mgr-add-service" className="eyw-btn !py-2 text-sm"><Plus size={16} /> New service</button>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-5">Everything about your center's services in one place — create, edit, assign enhancers, activate or delete.</p>

      {dupGroups.length > 0 && (
        <div className="mb-6 rounded-2xl border border-[#E0A458]/50 bg-[#E0A458]/10 p-4" data-testid="mgr-dup-banner">
          <p className="text-sm font-semibold text-[#B5794E] flex items-center gap-2"><AlertTriangle size={16} /> Possible duplicate services found</p>
          <div className="mt-2 space-y-2">
            {dupGroups.map((g, i) => (
              <div key={i} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`mgr-dup-group-${i}`}>
                <span className="text-sm text-[#4A5D6B]">“{g[0].title}” appears <strong>{g.length}×</strong></span>
                <button onClick={() => setMergeGroup(g)} data-testid={`mgr-dup-merge-${i}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs !border-[#B5794E] !text-[#B5794E]"><Merge size={13} /> Merge or clean up</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {roster.length > 0 && (
        <div className="eyw-card p-4 mb-6" data-testid="mgr-roster-availability">
          <p className="text-sm font-semibold text-[#1A242B] mb-3 flex items-center gap-2"><CalendarClock size={16} className="text-[#144F75]" /> Practitioner availability</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {roster.map((e) => {
              const slots = [...(e.availability || [])].sort((a, b) => (a.day - b.day) || String(a.start).localeCompare(String(b.start)));
              return (
                <div key={e.enhancer_id} className="rounded-xl border border-[#E5E0D8] p-3 flex items-start justify-between gap-3" data-testid={`avail-card-${e.enhancer_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A242B] truncate">{e.name || e.email || "Practitioner"}</p>
                    {slots.length === 0 ? (
                      <p className="text-xs text-[#B5794E] mt-1">No availability set — bookable any time a room is free</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {slots.map((s, i) => <span key={i} className="text-[11px] bg-[#EDF3F7] text-[#144F75] rounded-full px-2 py-0.5">{DAYS[s.day]} {s.start}–{s.end}</span>)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setAvailFor(e)} data-testid={`avail-open-${e.enhancer_id}`} className="shrink-0 rounded-full border border-[#E5E0D8] px-3 py-1.5 text-xs text-[#4A5D6B] hover:border-[#144F75] hover:text-[#144F75] flex items-center gap-1.5"><CalendarClock size={12} /> Edit hours</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <p className="text-[#4A5D6B] text-sm eyw-card p-6 text-center" data-testid="mgr-services-empty">No services yet. Create your first one.</p>
      ) : (
        <div className="space-y-3">
          {services.map((s) => (
            <ServiceRow key={s.id} service={s} roster={roster} assign={assignById[s.id]}
              isDup={dupIds.has(s.id)} onEdit={() => setModal(s)} onDelete={() => setDelTarget(s)}
              onToggle={() => togglePublished(s)} onSaved={refresh} />
          ))}
        </div>
      )}

      {modal && <ServiceModal service={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={refresh} />}
      {availFor && <AvailabilityModal enhancer={availFor} onClose={() => setAvailFor(null)} />}
      {mergeGroup && <MergeModal group={mergeGroup} onClose={() => setMergeGroup(null)} onDone={() => { setMergeGroup(null); refresh(); }} />}

      {delTarget && (
        <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={() => setDelTarget(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="mgr-service-delete-confirm">
            <h3 className="font-serif text-xl text-[#1A242B] mb-2">Delete “{delTarget.title}”?</h3>
            <p className="text-sm text-[#4A5D6B] mb-5">This removes the service from your center's storefront and its provider roster. This can't be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDelTarget(null)} data-testid="mgr-service-delete-cancel" className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} data-testid="mgr-service-delete-yes" className="eyw-btn !py-2 text-sm !bg-red-600 hover:!bg-red-700 disabled:opacity-60">{deleting && <Loader2 className="animate-spin" size={15} />} Delete service</button>
            </div>
          </div>
        </div>
      )}
      {blocked && (
        <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={() => setBlocked(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mgr-service-delete-blocked">
            <h3 className="font-serif text-xl text-[#1A242B] mb-2 flex items-center gap-2"><AlertTriangle size={20} className="text-[#D98E73]" /> Can't delete yet</h3>
            <p className="text-sm text-[#4A5D6B] mb-4">{blocked.message}</p>
            <div className="rounded-xl border border-[#E5E0D8] divide-y divide-[#E5E0D8] mb-5">
              {(blocked.bookings || []).map((b) => (
                <div key={b.id} className="px-3 py-2 flex items-center justify-between text-sm">
                  <span className="text-[#1A242B]">{fmtDate(b.date)} {b.start_time && `· ${fmtTimeRange(b.start_time)}`}</span>
                  <span className="text-[#4A5D6B] truncate max-w-[45%]">{b.name}</span>
                  <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F1EDE6] text-[#8FA89B]">{b.status}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#8FA89B] mb-4">Cancel or reschedule these from the Bookings tab, then delete the service — or merge it into another service to keep the bookings.</p>
            <div className="flex justify-end">
              <button onClick={() => setBlocked(null)} data-testid="mgr-service-blocked-close" className="eyw-btn !py-2 text-sm">Got it</button>
            </div>
          </div>
        </div>
      )}
      <SharedServicesPrices />
    </div>
  );
}

function ServiceRow({ service: s, roster, assign, isDup, onEdit, onDelete, onToggle, onSaved }) {
  const [open, setOpen] = useState(false);
  const initial = assign?.practitioner_ids || [];
  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);
  const rosterById = Object.fromEntries(roster.map((r) => [r.enhancer_id, r]));
  const names = initial.map((id) => rosterById[id]?.name).filter(Boolean);
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...initial].sort());

  const saveAssign = async () => {
    setSaving(true);
    try {
      const overrides = selected.map((id) => ({
        enhancer_id: id,
        price: assign?.overrides?.[id]?.price ?? null,
        duration_min: assign?.overrides?.[id]?.duration_min ?? null,
      }));
      await api.put(`/manager/services/${s.id}/practitioners`, { enhancer_ids: selected, overrides });
      toast.success("Enhancer assignment updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className={`eyw-card p-4 ${isDup ? "border border-[#E0A458]/60" : ""}`} data-testid={`mgr-usvc-${s.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 cursor-pointer" onClick={() => setOpen(!open)}>
          <p className="text-sm font-semibold text-[#1A242B] flex items-center gap-2 flex-wrap">
            {s.title}
            <span data-testid={`mgr-usvc-status-${s.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${s.published ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#F1EDE6] text-[#8FA89B]"}`}>{s.published ? "Active" : "Inactive"}</span>
            {isDup && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#E0A458]/20 text-[#B5794E] inline-flex items-center gap-1"><AlertTriangle size={9} /> Duplicate</span>}
            {s.access_level && s.access_level !== "public" && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#144F75]/[0.08] text-[#144F75] inline-flex items-center gap-0.5"><Lock size={9} /> {ACCESS_LABELS[s.access_level] || "Members"}</span>}
          </p>
          <p className="text-xs text-[#8FA89B] mt-0.5" data-testid={`mgr-usvc-enh-${s.id}`}>
            {money(Number(s.price || 0))} · {s.duration_min}m · {names.length ? `Enhancers: ${names.join(", ")}` : "No enhancers assigned"}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onToggle} data-testid={`mgr-usvc-toggle-${s.id}`} title={s.published ? "Deactivate" : "Activate"}
            className={`rounded-full px-3 py-1.5 text-xs border ${s.published ? "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#8FA89B]" : "border-[#5C8A72] text-[#5C8A72] hover:bg-[#5C8A72]/10"}`}>
            {s.published ? "Deactivate" : "Activate"}
          </button>
          <button onClick={onEdit} data-testid={`mgr-usvc-edit-${s.id}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-[#144F75]/10 text-[#144F75]"><Pencil size={15} /></button>
          <button onClick={onDelete} data-testid={`mgr-usvc-delete-${s.id}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>
          <button onClick={() => setOpen(!open)} data-testid={`mgr-usvc-expand-${s.id}`} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-[#F1EDE6] text-[#8FA89B]"><ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
          <div className="mb-4"><ServiceLocationsEditor serviceId={s.id} testid={`mgr-svc-locations-${s.id}`} /></div>
          <p className="text-xs font-semibold text-[#4A5D6B] uppercase tracking-wide mb-2 flex items-center gap-1.5"><Users size={13} /> Assign enhancers</p>
          {roster.length === 0 ? (
            <p className="text-xs text-[#8FA89B]">No enhancers on your roster yet — add them from the Enhancers tab.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-1.5 mb-3">
              {roster.map((e) => (
                <label key={e.enhancer_id} className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer rounded-lg hover:bg-[#F9F7F3] px-2 py-1.5" data-testid={`mgr-usvc-${s.id}-enh-${e.enhancer_id}`}>
                  <input type="checkbox" checked={selected.includes(e.enhancer_id)} className="accent-[#144F75]"
                    onChange={() => setSelected(selected.includes(e.enhancer_id) ? selected.filter((x) => x !== e.enhancer_id) : [...selected, e.enhancer_id])} />
                  {e.name || e.email}
                  {!e.has_availability && <span className="text-[10px] text-[#B5794E]">(no availability)</span>}
                </label>
              ))}
            </div>
          )}
          <button onClick={saveAssign} disabled={saving || !dirty} data-testid={`mgr-usvc-save-${s.id}`} className="eyw-btn !py-2 text-sm disabled:opacity-50 flex items-center gap-1.5">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Save assignment
          </button>
        </div>
      )}
    </div>
  );
}

function MergeModal({ group, onClose, onDone }) {
  const [keepId, setKeepId] = useState(group[0].id);
  const [busy, setBusy] = useState(false);
  const merge = async () => {
    setBusy(true);
    try {
      await api.post("/manager/services/merge", { keep_id: keepId, remove_ids: group.filter((s) => s.id !== keepId).map((s) => s.id) });
      toast.success("Duplicates merged — bookings and enhancers moved to the kept service");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="mgr-merge-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1 flex items-center gap-2"><Merge size={18} className="text-[#B5794E]" /> Merge “{group[0].title}”</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Choose which copy to keep. Bookings and enhancer assignments from the others move to it; the duplicates are deleted.</p>
        <div className="space-y-2 mb-5">
          {group.map((s) => (
            <label key={s.id} className={`flex items-center justify-between gap-3 rounded-xl border p-3 cursor-pointer ${keepId === s.id ? "border-[#144F75] bg-[#144F75]/[0.05]" : "border-[#E5E0D8]"}`} data-testid={`mgr-merge-opt-${s.id}`}>
              <span className="flex items-center gap-2 text-sm text-[#1A242B]">
                <input type="radio" name="keep" checked={keepId === s.id} onChange={() => setKeepId(s.id)} className="accent-[#144F75]" />
                {money(Number(s.price || 0))} · {s.duration_min}m {s.published ? "· Active" : "· Inactive"}
              </span>
              <span className="text-[11px] text-[#8FA89B]">created {String(s.created_at || "").slice(0, 10)}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={merge} disabled={busy} data-testid="mgr-merge-confirm" className="eyw-btn !py-2 text-sm disabled:opacity-60">{busy && <Loader2 className="animate-spin" size={15} />} Merge {group.length - 1} duplicate{group.length > 2 ? "s" : ""}</button>
        </div>
      </div>
    </div>
  );
}

export function ServiceModal({ service, onClose, onSaved }) {
  const catalogQ = useQuery({ queryKey: ["mgr-service-library"], queryFn: () => api.get("/manager/me/service-library").then((r) => r.data) });
  const catalog = catalogQ.data || [];
  const [form, setForm] = useState({
    title: service?.title || "", category: service?.category || "Wellness", short_description: service?.short_description || "",
    description: service?.description || "", duration_min: service?.duration_min || 60, price: service?.price || 0,
    image: service?.image || "",
    deposit_pct: service?.deposit_pct || 0, service_type: service?.service_type || "foundation",
    room: service?.room || "",
    access_level: service?.access_level || "public",
    allow_provider_choice: service?.allow_provider_choice || false, published: service?.published ?? true,
    enhancer_required: service?.enhancer_required !== false,
    enhancer_id: service?.enhancer_id || "",
  });
  const adoptCatalog = (id) => {
    const c = catalog.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({
      ...f,
      title: f.title || c.title,
      category: c.category || f.category,
      short_description: f.short_description || c.short_description || "",
      description: f.description || c.description,
      duration_min: c.duration_min || f.duration_min,
      price: f.price || c.price || 0,
      image: f.image || c.image || "",
    }));
    toast.success(`Started from “${c.title}” — edit anything before saving.`);
  };
  const rooms = useQuery({ queryKey: ["mgr-rooms"], queryFn: () => api.get("/manager/me/rooms").then((r) => r.data) });
  const modalSpQ = useQuery({ queryKey: ["mgr-service-practitioners"], queryFn: () => api.get("/manager/me/service-practitioners").then((r) => r.data) });
  const modalRoster = modalSpQ.data?.roster || [];
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (service) await api.put(`/manager/services/${service.id}`, form);
      else await api.post("/manager/services", form);
      toast.success("Service saved"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mgr-service-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-4">{service ? "Edit service" : "New center service"}</h3>
        <div className="space-y-3">
          {!service && catalog.length > 0 && (
            <div className="rounded-xl border border-[#144F75]/20 bg-[#144F75]/[0.04] p-3" data-testid="mgr-catalog-adopt-box">
              <label className="text-xs font-semibold text-[#144F75] mb-1 block">Start from the service library (optional)</label>
              <select defaultValue="" onChange={(e) => { if (e.target.value) adoptCatalog(e.target.value); e.target.value = ""; }} data-testid="mgr-catalog-select" className="eyw-input w-full text-sm">
                <option value="">Choose a shared service to pre-fill…</option>
                {catalog.map((c) => <option key={c.id} value={c.id}>{c.title}{c.category ? ` · ${c.category}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-[#8FA89B] mt-1">Pre-fills the fields — you can edit everything; it stays your center's own service.</p>
            </div>
          )}
          <div><label className="text-sm font-medium mb-1 block">Title</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mgr-service-title" className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Category</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="mgr-service-category" placeholder="e.g. Float Therapy, Reiki, Massage" className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Short description</label><input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="eyw-input w-full" /></div>
          <div><label className="text-sm font-medium mb-1 block">Full description</label>
            <RichTextEditor value={form.description} onChange={(html) => setForm({ ...form, description: html })} testid="mgr-service-description" enableBlocks={false} placeholder="Describe this service — add headings, lists, images and video…" minHeight={140} /></div>
          <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="Service photo (square)" crop="square" testid="mgr-service-image" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-sm font-medium mb-1 block">Duration (min)</label><input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })} data-testid="mgr-service-duration" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Price ($)</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} data-testid="mgr-service-price" className="eyw-input w-full" /></div>
            <div><label className="text-sm font-medium mb-1 block">Deposit %</label><input type="number" value={form.deposit_pct} onChange={(e) => setForm({ ...form, deposit_pct: parseInt(e.target.value) || 0 })} className="eyw-input w-full" /></div>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Type</label>
            <select value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} data-testid="mgr-service-type" className="eyw-input w-full">
              <option value="foundation">Foundation (center-provided)</option>
              <option value="shared">Shared (guest may choose provider)</option>
              <option value="signature">Signature</option>
            </select></div>
          <div><label className="text-sm font-medium mb-1 block">Room</label>
            <select value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="mgr-service-room" className="eyw-input w-full">
              <option value="">No specific room</option>
              {(rooms.data || []).map((r) => <option key={r.id} value={r.key}>{r.name}</option>)}
            </select>
            <p className="text-[11px] text-[#8FA89B] mt-1">Assigning a room enables conflict warnings & overrides when booking this service.</p>
          </div>
          <div><label className="text-sm font-medium mb-1 block">Access level</label>
            <select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} data-testid="mgr-service-access" className="eyw-input w-full">
              <option value="public">Public — any guest</option>
              <option value="discovery">Discovery members only</option>
              <option value="community">Community members only</option>
              <option value="all_members">All members</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.allow_provider_choice} onChange={(e) => setForm({ ...form, allow_provider_choice: e.target.checked })} /> Let guests choose their provider</label>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published (bookable)</label>
          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]" data-testid="mgr-service-enhancer-required"><input type="checkbox" checked={form.enhancer_required} onChange={(e) => setForm({ ...form, enhancer_required: e.target.checked })} /> Requires a specific enhancer <span className="text-xs text-[#8FA89B]">(uncheck for room-only services like float or sauna — slots follow center hours + room only)</span></label>
          {!form.enhancer_required && (
            <div data-testid="mgr-service-contact-enhancer-box">
              <label className="text-sm font-medium mb-1 block">Contact / supervising enhancer <span className="text-xs text-[#8FA89B] font-normal">(optional — not required for booking)</span></label>
              <select value={form.enhancer_id} onChange={(e) => setForm({ ...form, enhancer_id: e.target.value })} data-testid="mgr-service-contact-enhancer" className="eyw-input w-full">
                <option value="">— No contact assigned —</option>
                {modalRoster.map((e) => <option key={e.enhancer_id} value={e.enhancer_id}>{e.name || e.email}</option>)}
              </select>
              <p className="text-[11px] text-[#8FA89B] mt-1">Center hours govern this service's booking times; this enhancer is just the point of contact / supervisor.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mgr-service-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save</button>
        </div>
      </div>
    </div>
  );
}
